
import {
  Box,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  useRef,
  useState,
  type ChangeEvent,
  useEffect
} from "react";
import { useTranslation } from "react-i18next";
import {
  CardProfileBox,
  ContentBox,
  Description,
  TitleTypography
} from "./CardProfile.styles";
import { CardProfileProps } from "./CardProfile.types";
import {
  // Button,
  Icon,
  Link,
  Modal,
  Spinner
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import {
  useCvFormativeQuery,
  useGetProfileImageQuery,
  useMutationProfileImageQuery
} from "../../queries";
import { useAuthStore } from "../../stores";
import { getError } from "../../utils/error";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";
import { ButtonBookmark } from "../Button";
import { DeleteProfileImageModal } from "../DeleteProfileImageModal";

export function CardProfile({
  city,
  email,
  location,
  name,
  phone,
  surname
}: CardProfileProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { customLogicFe } = getEnvironmentVariables();

  const authenticationMode = useAuthStore(state => state.mode);
  const userId = useAuthStore(state => state.userData?.id_user);
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const [modalText, setModalText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteProfileImageModal, setShowDeleteProfileImageModal] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  const { data: profileImage, refetch: refetchProfileImage } = useGetProfileImageQuery({});

  const { data: cvData, isLoading: isFetchingCVData } = useCvFormativeQuery({
    studentId: userId?.toString() ?? "0"
  });

  const {
    mutate: mutateProfileImage,
    error:mutateProfileError,
    isSuccess:mutateProfileIsSuccess,
    isLoading:mutateProfileIsLoading,
    isError : mutateProfileIsError
  } = useMutationProfileImageQuery();

  const acceptFileType = "image/*";

  useEffect(()=> {
    if (mutateProfileIsError && mutateProfileError){
      const errorModal = getError(mutateProfileError)?.[0]?.message;
      setShowModal(true);
      if(errorModal){
        const ismodalForbiddenText = isJSON(errorModal) && JSON.parse(errorModal)?.message === "Forbidden";
        if(ismodalForbiddenText){
          setModalText(t("forbidden_image"));
        }
        else{
          setModalText( errorModal?.toString() ?? "generic error");
        }
      }

    } else if( mutateProfileIsLoading){
      setSpinnerLoading(true);
    } else {
      setSpinnerLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    mutateProfileIsError,
    mutateProfileError,
    mutateProfileIsSuccess,
    mutateProfileIsLoading
  ]
  );

  return (
    <>
      <CardProfileBox role="region" aria-label={ t("your_data") }>
        <TitleTypography tabIndex={ 0 } id="title-card-profile">
          { t("your_data") }
        </TitleTypography>
        <ContentBox>
          <Box
            borderRadius= "8px"
            height="fit-content"
            lineHeight= "0.1"
            position= "relative"
            width= { isMobile ? "106px" : "30%" }

            sx={ {
              "> :not(button)":{
                borderRadius: "8px",
                objectFit: "cover",
                width: "100%",
                [theme.breakpoints.down("sm")]:{
                  height: "99px",
                  width: "106px"
                }
              }
            } }
          >
            { profileImage && (
              <ButtonBookmark
                aria-label={ t("profile_delete_avatar") }
                onClick={ () => setShowDeleteProfileImageModal(true) }
                variant="contained"
                sx={ {
                  ".icon path":{
                    fill: theme.customColors.textPrimaryCta
                  },
                  borderRadius: theme.spacing(1),
                  top: theme.spacing(-1),
                  boxShadow: "none",
                  minWidth: "unset",
                  padding: theme.spacing(1),
                  position: "absolute",
                  right: theme.spacing(-1)
                } }
              >
                <Icon
                  icon="trash"
                  size="1rem"
                />
              </ButtonBookmark>
            ) }
            {
              spinnerLoading ? (
                <Spinner/>
              ) : (
                profileImage ? (
                  <img
                    src={ profileImage }
                    alt={ `${t("avatar_user")} ${name} ${surname}` }
                    style={ {
                      aspectRatio: "1",
                      backgroundColor: theme.palette.grey?.[400]
                    } }
                    loading="lazy"
                  />
                ) : (
                  <Stack
                    justifyContent="center"
                    sx={ {
                      aspectRatio: "1",
                      backgroundColor: theme.palette.grey?.[400]
                    } }
                  >
                    <Typography
                      aria-label={ `${t("avatar_user")} ${name} ${surname}` }
                      tabIndex={ 0 }
                      fontWeight={ 700 }
                      fontSize={ isMobile ? "3rem" : "5rem" }
                      margin={ theme.spacing(0) }
                      color={ theme.customColors.backgroundPrimaryLighter }
                      textAlign="center"
                      textTransform="uppercase"
                      width="100%"
                    >
                      { name?.charAt(0) + surname?.charAt(0) }
                    </Typography>
                  </Stack>
                )
              )
            }
            <ButtonBookmark
              aria-label={ t("upload_file") }
              onClick={ ()=> handleUploadClick() }
              onKeyDown={ (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleUploadClick();
                }
              } }
              variant="contained"
              sx={ {
                ".icon path":{
                  fill: theme.customColors.textPrimaryCta
                },
                borderRadius: theme.spacing(1),
                bottom: theme.spacing(-1),
                boxShadow: "none",
                minWidth: "unset",
                padding: theme.spacing(1),
                position: "absolute",
                right: theme.spacing(-1)
              } }
            >
              <input
                aria-label={ t("upload_file") }
                accept={ acceptFileType }
                className="FileUpload"
                type="file"
                ref={ hiddenFileInput }
                onChange={ handleFileChange }
                style={ { display: "none" } }
              />
              <Icon
                aria-hidden="true"
                icon="Icons_modifica"
                size="1rem"
              />
            </ButtonBookmark>
          </Box>
          <Description>
            <Typography
              tabIndex={ 0 }
              aria-label={ `${t("your_name")} ${name} ${" "} ${surname}` }
              fontSize={ "32px" }
              fontWeight={ 700 }
              lineHeight={ 1.334 }
              margin={ 0 }
              paddingBottom={ "8px" }
              variant="h1"
              color={ theme.customColors.textWidget }
            >
              { name } { surname }
            </Typography>
            {
              authenticationMode !== "SSO" ? (
                <Link aria-label={ t("change_password") } href="/access/password/change">
                  { t("change_password") }
                </Link>
              ) : null
            }
            {
              email ? (
                <Typography
                  tabIndex={ 0 }
                  aria-label={ `${t("your_email")} ${email}` }
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  color={ theme.customColors.textWidget }
                >
                  { email }
                </Typography>
              ) : null
            }
            {
              phone ? (
                <Typography
                  tabIndex={ 0 }
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  { phone }
                </Typography>
              ) : null
            }
            {
              (city || location) ? (
                <Box
                  display="flex"
                  lineHeight="normal"
                >
                  {
                    city ? (
                      <Typography
                        fontWeight="700"
                        paddingRight="3px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        { city }
                      </Typography>
                    ) : null
                  }
                  {
                    location ? (
                      <Typography >
                        { location }
                      </Typography>
                    ) : null
                  }
                </Box>
              ) : null
            }
            { !checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hide_cv_formativo" }) &&
              <ButtonDigited
                typology="primary"
                buttonSize="md"
                aria-label={ t("download_formative_cv") }
                onClick={ () => {
                  if (!cvData) return;
                  const blob = new Blob([cvData], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `${ name }_${ surname }_cv.csv`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                } }
                disabled={ isFetchingCVData || !cvData }
              >
                { isFetchingCVData ?
                  <Box sx={ { display:"flex",gap:2 } }><Spinner size={ 14 } padding={ 0 } sx={ { color: theme.customColors.textDisabledAlternative } } /> { t("download_formative_cv") }</Box>
                  : <Typography sx={ { padding: "0 !important", color: `${theme.customColors.textPrimaryCta} !important` } }>{ t("download_formative_cv") }</Typography> }
              </ButtonDigited> }
          </Description>
        </ContentBox>
      </CardProfileBox>
      <Modal
        open={ showModal }
        onClose={ ()=> {
          setShowModal(false);
          setSpinnerLoading(false);
        } }
        description=""
        title={ modalText }
      />
      <DeleteProfileImageModal onSuccess={ () => {
        refetchProfileImage();
        if(hiddenFileInput.current) hiddenFileInput.current.value = "";
      } } open={ showDeleteProfileImageModal } closeDrawer={ () => setShowDeleteProfileImageModal(false) } />
    </>
  );

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const reader = new FileReader();
      reader.readAsDataURL(e.target.files[0]);
      const fileContent = e.target.files[0];
      reader.onload = () => {
        const formdata = new FormData();
        if (fileContent){
          if(fileContent.size > 2000000 ){
            setShowModal(true);
            setModalText(t("file_bigger_text") );
          } else {
            formdata.append("file", fileContent, fileContent.name);
            mutateProfileImage(formdata);
          }
        }
      };
    }
  }

  function handleUploadClick() {
    if (hiddenFileInput.current){
      hiddenFileInput.current.click();
    }
    // if (!file) {
    //   return;
    // }
  }

  function isJSON(str:string) {
    try {
      JSON.stringify(JSON.parse(str));
      return true;
    } catch (e) {
      return false;
    }
  }
}
